import { z } from 'zod';

/**
 * コメントタイプ
 * - manual: 手動入力
 * - status_change: ステータス変更
 * - assignment: 担当者変更
 * - approval: 承認関連
 */
export const ScheduleCommentTypeSchema = z.enum([
  'manual',
  'status_change',
  'assignment',
  'approval'
]);
export type ScheduleCommentType = z.infer<typeof ScheduleCommentTypeSchema>;

/**
 * スケジュールコメント
 */
export const ScheduleCommentSchema = z.object({
  id: z.string().uuid(),
  scheduleId: z.string().uuid(),
  companyId: z.string().uuid(),
  userId: z.string().uuid(),
  content: z.string(),
  commentType: ScheduleCommentTypeSchema,
  statusFrom: z.string().nullable().optional(),
  statusTo: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().uuid().nullable().optional(),
  // ユーザー情報（拡張）
  user: z.object({
    id: z.string().uuid(),
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    avatarUrl: z.string().nullable().optional(),
  }).optional(),
});
export type ScheduleComment = z.infer<typeof ScheduleCommentSchema>;

/**
 * スケジュールステータス
 */
export const ScheduleStatusSchema = z.enum([
  'scheduled',
  'in_progress',
  'pending_approval',
  'completed',
  'cancelled',
  'postponed',
]);
export type ScheduleStatus = z.infer<typeof ScheduleStatusSchema>;

/**
 * コメント作成入力
 */
export const CreateScheduleCommentInputSchema = z.object({
  scheduleId: z.string().uuid(),
  content: z.string().min(1, 'コメントを入力してください'),
  /** ステータス変更（任意） - 設定するとステータスも同時に変更される */
  newStatus: ScheduleStatusSchema.optional(),
});
export type CreateScheduleCommentInput = z.infer<typeof CreateScheduleCommentInputSchema>;

/**
 * コメント更新入力
 */
export const UpdateScheduleCommentInputSchema = z.object({
  commentId: z.string().uuid(),
  content: z.string().min(1, 'コメントを入力してください'),
});
export type UpdateScheduleCommentInput = z.infer<typeof UpdateScheduleCommentInputSchema>;

/**
 * 自動コメント生成入力（内部用）
 */
export interface CreateAutoCommentInput {
  scheduleId: string;
  companyId: string;
  userId: string;
  commentType: ScheduleCommentType;
  statusFrom?: string | null;
  statusTo?: string | null;
  content: string;
}

/**
 * ステータスラベルマップ
 */
export const STATUS_LABELS: Record<string, string> = {
  scheduled: '予定',
  in_progress: '実行中',
  pending_approval: '承認待ち',
  completed: '完了',
  cancelled: 'キャンセル',
  postponed: '延期',
};

/**
 * コメントタイプラベルマップ
 */
export const COMMENT_TYPE_LABELS: Record<ScheduleCommentType, string> = {
  manual: '手動',
  status_change: 'ステータス変更',
  assignment: '担当者変更',
  approval: '承認',
};
